/**
 * The checks from the boxed TCG Asset Group Creator, with no database access, so the dashboard
 * can preview a CSV in the browser. The writer that turns a passing row into a catalog item is
 * `bulk-create.ts`.
 */

/** How many rows one import accepts. The dashboard parser and the tRPC input use the same cap. */
export const MAX_TCG_IMPORT_ROWS = 1000;

/**
 * The card games the creator's select offered, as the codes a CSV cell has to match. Anything
 * else was "invalid card game".
 */
export const TCG_CARD_GAMES = [
  "POKEMON",
  "MTG",
  "YUGIOH",
  "ONE_PIECE",
  "LORCANA",
  "RIFTBOUND",
  "DRAGON_BALL",
  "STAR_WARS",
  "DIGIMON",
  "GUNDAM",
] as const;

export type TcgCardGame = (typeof TCG_CARD_GAMES)[number];

const POKEMON_PRINTINGS = [
  "Normal",
  "Unlimited Holofoil",
  "1st Edition Holofoil",
  "Holofoil",
  "1st Edition",
  "Unlimited",
  "Reverse Holofoil",
] as const;

const LORCANA_PRINTINGS = ["Normal", "Cold Foil", "Holofoil"] as const;

const YUGIOH_PRINTINGS = ["Normal", "1st Edition"] as const;

/** MTG and every game the creator did not give its own list. */
const DEFAULT_PRINTINGS = ["Normal", "Foil"] as const;

const PRINTINGS: Partial<Record<TcgCardGame, readonly string[]>> = {
  POKEMON: POKEMON_PRINTINGS,
  LORCANA: LORCANA_PRINTINGS,
  YUGIOH: YUGIOH_PRINTINGS,
};

/** The printings the creator offered for that game. */
export function printingsForCardGame(game: TcgCardGame): readonly string[] {
  return PRINTINGS[game] ?? DEFAULT_PRINTINGS;
}

export const TCG_IMPORT_ISSUES = [
  "name_blank",
  "product_id_blank",
  "invalid_card_game",
  "invalid_printing",
  "duplicate_filter",
] as const;

export type TcgImportIssue = (typeof TCG_IMPORT_ISSUES)[number];

/** The creator's own refusal text, one message per cause. */
export const TCG_IMPORT_ISSUE_MESSAGES: Record<TcgImportIssue, string> = {
  name_blank: "name is blank",
  product_id_blank: "product id is blank",
  invalid_card_game: "invalid card game",
  invalid_printing: "invalid printing",
  duplicate_filter: "duplicate filter",
};

export interface TcgImportRow {
  name: string;
  productId: string;
  cardGame: string;
  printing: string;
}

export interface TcgImportResult {
  /** Index in the rows that were submitted. */
  index: number;
  name: string;
  outcome: "created" | TcgImportIssue;
  id: string | null;
}

export interface PreparedTcgImport {
  name: string;
  productId: string;
  cardGame: TcgCardGame;
  printing: string;
}

function cardGame(value: string): TcgCardGame | null {
  const code = value.trim().toUpperCase();
  return (TCG_CARD_GAMES as readonly string[]).includes(code) ? (code as TcgCardGame) : null;
}

/**
 * The first reason the creator would have refused this row, before the duplicate lookup. Blank
 * name, then blank product id, then the game, then the printing. Null when the row can be created.
 */
export function tcgImportIssue(row: TcgImportRow): TcgImportIssue | null {
  if (row.name.trim() === "") return "name_blank";
  if (row.productId.trim() === "") return "product_id_blank";
  const game = cardGame(row.cardGame);
  if (!game) return "invalid_card_game";
  const printing = row.printing.trim();
  if (!printingsForCardGame(game).includes(printing)) return "invalid_printing";
  return null;
}

/** The row, trimmed and canonical, or the reason it cannot be created. */
export function prepareTcgImport(row: TcgImportRow): PreparedTcgImport | TcgImportIssue {
  const issue = tcgImportIssue(row);
  if (issue) return issue;
  const game = cardGame(row.cardGame);
  if (!game) return "invalid_card_game";
  return {
    name: row.name.trim(),
    productId: row.productId.trim(),
    cardGame: game,
    printing: row.printing.trim(),
  };
}

export function isPreparedTcgImport(
  value: PreparedTcgImport | TcgImportIssue,
): value is PreparedTcgImport {
  return typeof value !== "string";
}
